import { useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { BellRing, Info } from "lucide-react";
import { Button } from "@ec2/ui";
import { useT } from "@ec2/i18n/react";
import type { MessageKey } from "@ec2/i18n";
import { appConfig } from "../../lib/config";
import { generalError, type LocalizedError } from "../../lib/errors";
import { currentPlatform, isIos } from "../../lib/platform";
import {
  existingPushSubscription,
  isStandalone,
  pushSupported,
  subscribeToPush,
} from "../../lib/pwa";
import { useSession } from "../../state/session";
import { ErrorLine } from "../common";
import { InstallGuide } from "./InstallGuide";

type Permission = NotificationPermission | "unsupported";

/** Where to re-allow blocked notifications: by OS, since the installed app has no browser UI. */
function deniedHelpKey(userAgent: string): MessageKey {
  if (/iPhone|iPad|iPod/.test(userAgent)) return "ec2.notify.denied.ios";
  if (/Android/.test(userAgent)) return "ec2.notify.denied.android";
  return "ec2.notify.denied.desktop";
}

/**
 * The notifications quest's page body. On an iPhone outside the installed app
 * push does not exist, so it shows the install guide first; otherwise one
 * button asks for permission and subscribes, which solves the quest
 * server-side (push.subscribe → bucksAwarded).
 */
export function NotifyGuide({ eventId }: { eventId: string }) {
  const t = useT();
  const queryClient = useQueryClient();
  const { api } = useSession();
  const standalone = isStandalone();
  const platform = currentPlatform(standalone);
  const supported = pushSupported() && Boolean(appConfig.vapidPublicKey);
  const [permission, setPermission] = useState<Permission>(() =>
    supported ? Notification.permission : "unsupported",
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<LocalizedError | null>(null);

  const send = async (subscription: Awaited<ReturnType<typeof existingPushSubscription>>) => {
    if (!subscription) return;
    const { bucksAwarded } = await api.push.subscribe.mutate({ eventId, ...subscription });
    if (bucksAwarded > 0) await queryClient.invalidateQueries();
  };

  // Permission granted earlier (e.g. from the profile): re-send it, which solves the quest.
  useEffect(() => {
    if (permission !== "granted") return;
    void existingPushSubscription()
      .then(send)
      .catch(() => undefined);
  }, [permission, eventId]);

  if (!supported) {
    if (isIos(platform) && !standalone) {
      return (
        <div className="flex flex-col gap-4" data-testid="ec-notify-ios-install">
          <p className="flex gap-2 rounded-xl bg-surface-raised p-3 text-[13px] text-fg">
            <Info size={16} aria-hidden className="mt-0.5 shrink-0 text-accent" />
            {t("ec2.notify.iosInstallFirst")}
          </p>
          <InstallGuide eventId={eventId} />
        </div>
      );
    }
    return (
      <p className="flex gap-2 text-base text-fg" data-testid="ec-notify-unsupported">
        <Info size={18} aria-hidden className="mt-0.5 shrink-0 text-fg-secondary" />
        {t("ec2.notify.unsupported")}
      </p>
    );
  }

  if (permission === "denied") {
    return (
      <div className="flex flex-col gap-2" data-testid="ec-notify-denied">
        <p className="text-base font-medium text-fg">{t("ec2.notify.denied")}</p>
        <p className="text-base text-fg-secondary">{t(deniedHelpKey(navigator.userAgent))}</p>
      </div>
    );
  }

  if (permission === "granted") {
    return <p className="text-base text-fg">{t("ec2.notify.done")}</p>;
  }

  const enable = async () => {
    setBusy(true);
    setError(null);
    try {
      const subscription = await subscribeToPush(appConfig.vapidPublicKey!);
      setPermission(Notification.permission);
      await send(subscription);
    } catch (err) {
      setError(generalError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-1" data-testid="ec-notify-guide">
      <Button
        variant="primary"
        disabled={busy}
        onClick={() => void enable()}
        data-testid="ec-notify-enable"
      >
        <BellRing size={18} aria-hidden /> {t("ec2.notify.button")}
      </Button>
      <p className="text-[13px] text-fg-secondary">{t("ec2.notify.buttonHint")}</p>
      {error && <ErrorLine error={error} />}
    </div>
  );
}
